import { Heading, Pill, Stack, Table, TableColumns, Text } from '@sixfold/common-ui';
import React, { useMemo } from 'react';
import { NavLink } from 'react-router';

import { CompanyGroupCompany, CompanyGroupLink } from './company_group_graph';
import { Routes } from '../../routes';

interface Props {
  companies: CompanyGroupCompany[];
  links: CompanyGroupLink[];
}

interface MemberRow {
  id: string;
  company: CompanyGroupCompany;
  role: Pick<CompanyGroupCompany, 'isHub' | 'isMember'>;
  linkId: string | null;
}

const columns: TableColumns<MemberRow> = [
  {
    id: 'company',
    title: 'Company',
    cellContent: ({ row }) => (
      <Stack direction="row" gap="xsmall" alignItems="center">
        <CompanyLink company={row.company} />
        {row.company.isCurrent && <Pill kind="info">This company</Pill>}
        {row.company.isTest && <Pill kind="warning">Test</Pill>}
      </Stack>
    ),
  },
  {
    id: 'id',
    title: 'Company ID',
    cellContent: ({ row }) => <>#{row.company.id}</>,
  },
  {
    id: 'role',
    title: 'Role',
    cellContent: ({ value }) => (
      <Stack direction="row" gap="xsmall" alignItems="center">
        {value.isHub && <Pill kind="success">Hub</Pill>}
        {value.isMember && <Pill kind="neutral">Member</Pill>}
      </Stack>
    ),
  },
  {
    id: 'linkId',
    title: 'Link #',
    cellContent: ({ value }) => <>{value ?? '-'}</>,
  },
];

export const CompanyGroupMembers: React.FC<Props> = ({ companies, links }) => {
  const rows = useMemo(() => buildRows(companies, links), [companies, links]);
  const memberCount = companies.filter((company) => company.isMember).length;

  return (
    <Stack direction="column" gap="small">
      <Text size="small">
        {companies.length} linked accounts · {memberCount} {memberCount === 1 ? 'member' : 'members'}
      </Text>
      <Table<MemberRow> columns={columns} data={rows} bordersOutside />
    </Stack>
  );
};

export const CompanyGroupHub: React.FC<{ companies: CompanyGroupCompany[] }> = ({ companies }) => {
  const hub = companies.find((company) => company.isHub);

  if (hub === undefined) {
    return null;
  }

  return (
    <Heading size="small">
      Company Group: <CompanyLink company={hub} />
    </Heading>
  );
};

const CompanyLink: React.FC<{ company: CompanyGroupCompany }> = ({ company }) => (
  <NavLink to={Routes.Company.generatePath({ company_id: company.id })}>{company.name ?? 'Unnamed company'}</NavLink>
);

function buildRows(companies: CompanyGroupCompany[], links: CompanyGroupLink[]): MemberRow[] {
  return companies
    .map((company) => ({
      id: company.id,
      company,
      role: { isHub: company.isHub, isMember: company.isMember },
      linkId: links.find((link) => link.memberId === company.id)?.id ?? null,
    }))
    .sort(
      (a, b) =>
        Number(b.company.isHub) - Number(a.company.isHub) || (a.company.name ?? '').localeCompare(b.company.name ?? ''),
    );
}
